export type DomPosition = {node: Node; offset: number};

interface ComposerUnit {
    text: string;
    node: Node | null;
    atomic: boolean;
    domBefore: DomPosition;
    domAfter: DomPosition;
}

const isMentionPill = (node: Node | null): boolean =>
    node !== null && node.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).classList.contains('mention');

const isLineBreak = (node: Node | null): boolean =>
    node !== null && node.nodeType === Node.ELEMENT_NODE && (node as HTMLElement).tagName === 'BR';

const isBlockContainer = (node: Node | null): boolean =>
    node !== null && node.nodeType === Node.ELEMENT_NODE &&
    ((node as HTMLElement).tagName === 'DIV' || (node as HTMLElement).tagName === 'P');

const isSoleLineBreakPlaceholder = (node: HTMLElement): boolean =>
    node.childNodes.length === 1 && isLineBreak(node.childNodes[0]);

const isTrailingBrPlaceholder = (node: HTMLElement): boolean =>
    node.nextSibling === null && isLineBreak(node.previousSibling);

const computeUnits = (root: Node): ComposerUnit[] => {
    const units: ComposerUnit[] = [];

    const pushText = (node: Text, parent: Node, index: number) => {
        units.push({
            text: node.textContent ?? '',
            node,
            atomic: false,
            domBefore: {node: parent, offset: index},
            domAfter: {node: parent, offset: index + 1},
        });
    };

    const pushAtEdge = (parent: Node, index: number, text: string) => {
        units.push({
            text,
            node: null,
            atomic: false,
            domBefore: {node: parent, offset: index},
            domAfter: {node: parent, offset: index + 1},
        });
    };

    const pushPill = (node: HTMLElement, parent: Node, index: number) => {
        units.push({
            text: node.textContent ?? '',
            node,
            atomic: true,
            domBefore: {node: parent, offset: index},
            domAfter: {node: parent, offset: index + 1},
        });
    };

    const pushBlockBreak = (node: HTMLElement, parent: Node, index: number) => {
        units.push({
            text: node.previousSibling !== null ? '\n' : '',
            node: null,
            atomic: false,
            domBefore: {node: parent, offset: index},
            domAfter: {node, offset: 0},
        });
    };

    const pushPlaceholderAnchor = (node: HTMLElement) => {
        units.push({
            text: '',
            node: null,
            atomic: false,
            domBefore: {node, offset: 0},
            domAfter: {node, offset: 1},
        });
    };

    const walkChildren = (parent: Node): void => {
        let index = 0;
        parent.childNodes.forEach(child => {
            walk(child, parent, index);
            index += 1;
        });
    };

    function walk(node: Node, parent: Node, index: number): void {
        if (node.nodeType === Node.TEXT_NODE) {
            pushText(node as Text, parent, index);
            return;
        }
        if (node.nodeType !== Node.ELEMENT_NODE) {
            return;
        }
        const el = node as HTMLElement;
        if (isMentionPill(el)) {
            pushPill(el, parent, index);
            return;
        }
        if (isLineBreak(el)) {
            pushAtEdge(parent, index, isTrailingBrPlaceholder(el) ? '' : '\n');
            return;
        }
        if (isBlockContainer(el)) {
            pushBlockBreak(el, parent, index);
            if (isSoleLineBreakPlaceholder(el)) {
                pushPlaceholderAnchor(el);
                return;
            }
            walkChildren(el);
            return;
        }
        walkChildren(el);
    }

    walkChildren(root);
    return units;
};

export const domToPlainText = (root: Node): string => {
    let text = '';
    for (const unit of computeUnits(root)) {
        text += unit.text;
    }
    return text;
};

interface TextLookupEntry {
    start: number;
    length: number;
    atomic: boolean;
}

interface ComposerIndex {
    units: ComposerUnit[];
    textIndex: Map<Node, TextLookupEntry>;
    boundaryIndex: Map<Node, Map<number, number>>;
    length: number;
}

const buildIndex = (root: Node): ComposerIndex => {
    const units = computeUnits(root);
    const textIndex = new Map<Node, TextLookupEntry>();
    const boundaryIndex = new Map<Node, Map<number, number>>();

    const setBoundary = (position: DomPosition, offset: number) => {
        let byOffset = boundaryIndex.get(position.node);
        if (!byOffset) {
            byOffset = new Map<number, number>();
            boundaryIndex.set(position.node, byOffset);
        }
        if (!byOffset.has(position.offset)) {
            byOffset.set(position.offset, offset);
        }
    };

    let total = 0;
    for (const unit of units) {
        if (unit.node !== null) {
            textIndex.set(unit.node, {start: total, length: unit.text.length, atomic: unit.atomic});
        }
        setBoundary(unit.domBefore, total);
        setBoundary(unit.domAfter, total + unit.text.length);
        total += unit.text.length;
    }

    return {units, textIndex, boundaryIndex, length: total};
};

const findAncestorPill = (node: Node, root: Node): HTMLElement | null => {
    let current: Node | null = node === root ? null : node.parentNode;
    while (current && current !== root) {
        if (isMentionPill(current)) {
            return current as HTMLElement;
        }
        current = current.parentNode;
    }
    return null;
};

export const domPositionToPlainOffset = (root: Node, node: Node, offset: number): number => {
    if (!root.contains(node)) {
        return 0;
    }

    const index = buildIndex(root);

    const textEntry = index.textIndex.get(node);
    if (textEntry) {
        if (textEntry.atomic) {
            return offset === 0 ? textEntry.start : textEntry.start + textEntry.length;
        }
        return textEntry.start + Math.max(0, Math.min(offset, textEntry.length));
    }

    const pillAncestor = findAncestorPill(node, root);
    if (pillAncestor) {
        const pillEntry = index.textIndex.get(pillAncestor);
        if (pillEntry) {
            return offset === 0 ? pillEntry.start : pillEntry.start + pillEntry.length;
        }
    }

    const boundaryMatch = index.boundaryIndex.get(node)?.get(offset);
    if (boundaryMatch !== undefined) {
        return boundaryMatch;
    }

    const range = document.createRange();
    let total = 0;
    for (const unit of index.units) {
        range.setStart(unit.domBefore.node, unit.domBefore.offset);
        range.collapse(true);
        if (range.comparePoint(node, offset) <= 0) {
            return total;
        }
        range.setStart(unit.domAfter.node, unit.domAfter.offset);
        range.collapse(true);
        if (range.comparePoint(node, offset) <= 0) {
            return total + unit.text.length;
        }
        total += unit.text.length;
    }

    return total;
};

export const plainOffsetToDomPosition = (root: Node, targetOffset: number): DomPosition => {
    const units = computeUnits(root);
    let total = 0;

    for (const unit of units) {
        const length = unit.text.length;
        if (targetOffset <= total) {
            return unit.domBefore;
        }
        if (targetOffset < total + length) {
            if (unit.node !== null && !unit.atomic) {
                return {node: unit.node, offset: targetOffset - total};
            }
            const before = targetOffset - total;
            const after = length - before;
            return before <= after ? unit.domBefore : unit.domAfter;
        }
        total += length;
    }

    return units.length > 0
        ? units[units.length - 1].domAfter
        : {node: root, offset: root.childNodes.length};
};

export const getPlainTextSelection = (root: HTMLElement): {start: number; end: number} | null => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
        return null;
    }
    const range = selection.getRangeAt(0);
    if (!root.contains(range.startContainer) || !root.contains(range.endContainer)) {
        return null;
    }
    const anchor = domPositionToPlainOffset(root, range.startContainer, range.startOffset);
    const focus = domPositionToPlainOffset(root, range.endContainer, range.endOffset);
    return {start: Math.min(anchor, focus), end: Math.max(anchor, focus)};
};

export const setPlainTextSelection = (root: HTMLElement, start: number, end: number): void => {
    const selection = window.getSelection();
    if (!selection) {
        return;
    }
    const from = plainOffsetToDomPosition(root, start);
    const to = start === end ? from : plainOffsetToDomPosition(root, end);
    const range = document.createRange();
    range.setStart(from.node, from.offset);
    range.setEnd(to.node, to.offset);
    selection.removeAllRanges();
    selection.addRange(range);
};
